import { useCallback, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { Archive, CheckSquare, FileText, FolderOpen, Link2, Plus, Search, X } from 'lucide-react'
import { v7 as uuidv7 } from 'uuid'
import type { LinkableEntityType } from '@/lib/constants/entityTypes'
import type { EnrichedEntityLink } from '@/lib/db/rxdb/hooks/entities/entity-links'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { CollapsibleSectionHeader } from '@/components/app/layout/collapsible-section-header'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import {
  useEntityLinkMutations,
  useEntityLinks,
  usePages,
  useProjects,
  useTask,
  useTasks,
} from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { ENTITY_TYPES, LINKABLE_ENTITY_LABELS } from '@/lib/constants/entityTypes'
import { LINK_TYPES } from '@/lib/db/rxdb/schemas/entityLink'
import { ProjectIcon } from '@/lib/utils/projectIcon'

interface ExplicitLinksSectionProps {
  entityId: string
  entityType: string // backend entity type: 'work_item' | 'document' | 'project'
  entityName: string
  workspaceId: string | null | undefined
  onLinkClick?: (entityId: string, entityType: string) => void
  /** Open with the link picker showing, e.g. from a doc's "No links" footer. */
  defaultAdding?: boolean
}

const ENTITY_TYPE_ICONS: Record<string, typeof FileText> = {
  [ENTITY_TYPES.WORK_ITEM]: CheckSquare,
  [ENTITY_TYPES.DOCUMENT]: FileText,
  [ENTITY_TYPES.PROJECT]: FolderOpen,
}

function getLinkableEntityLabel(entityType: string) {
  if (entityType in LINKABLE_ENTITY_LABELS) {
    return LINKABLE_ENTITY_LABELS[entityType as LinkableEntityType]
  }

  return entityType
}

function LinkEntityIcon({ link }: { link: EnrichedEntityLink }) {
  if (!link.resolvedTargetIcon) return null

  if (link.targetEntityType === ENTITY_TYPES.WORK_ITEM) {
    return (
      <span className="text-muted-foreground mr-1.5 text-xs">
        {link.resolvedTargetIcon}
      </span>
    )
  }

  if (
    link.targetEntityType === ENTITY_TYPES.DOCUMENT ||
    link.targetEntityType === ENTITY_TYPES.PROJECT
  ) {
    return (
      <ProjectIcon
        iconType={link.resolvedTargetIconType}
        icon={link.resolvedTargetIcon}
        iconColor={link.resolvedTargetIconColor}
        className="inline mr-1.5 -mt-0.5 text-muted-foreground"
        size={14}
      />
    )
  }

  return null
}

export function ExplicitLinksSection({
  entityId,
  entityType,
  entityName,
  workspaceId,
  onLinkClick,
  defaultAdding = false,
}: ExplicitLinksSectionProps) {
  const [isAdding, setIsAdding] = useState(defaultAdding)
  const [isOpen, setIsOpen] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery] = useDebounceValue(searchQuery, 200)
  const [filterType, setFilterType] = useState<string>('all')

  const { links, isLoading } = useEntityLinks(entityId)
  const { createEntityLink, deleteEntityLink } = useEntityLinkMutations()
  const { task: currentTask } = useTask(
    entityType === ENTITY_TYPES.WORK_ITEM ? entityId : undefined,
  )
  const { tasks } = useTasks({ isDeleted: false })
  const { pages } = usePages()
  const { projects } = useProjects()
  const userId = useLoginStore((state) => state.userId)
  const owningProjectId = currentTask?.projectId ?? null

  // Only show explicit links. For tasks, the owning project is already shown in
  // task metadata, so hide any duplicate explicit project link to that owner.
  const explicitLinks = useMemo(
    () =>
      links.filter(
        (l) =>
          l.linkType === LINK_TYPES.EXPLICIT &&
          !(
            entityType === ENTITY_TYPES.WORK_ITEM &&
            l.targetEntityType === ENTITY_TYPES.PROJECT &&
            l.targetEntityId === owningProjectId
          ),
      ),
    [links, entityType, owningProjectId],
  )

  const linkedEntityIds = useMemo(
    () => new Set(explicitLinks.map((l) => l.targetEntityId)),
    [explicitLinks],
  )

  // Build searchable entity list
  const searchResults = useMemo(() => {
    if (debouncedQuery.trim().length < 3) return []
    const query = debouncedQuery.toLowerCase()

    const results: Array<{
      id: string
      name: string
      icon: string | null
      entityType: string
    }> = []

    if (filterType === 'all' || filterType === ENTITY_TYPES.WORK_ITEM) {
      for (const t of tasks) {
        if (
          t.id !== entityId &&
          !linkedEntityIds.has(t.id) &&
          (!workspaceId || t.envId === workspaceId) &&
          (t.name.toLowerCase().includes(query) ||
            (t as any).identifier?.toLowerCase().includes(query))
        ) {
          results.push({
            id: t.id,
            name: t.name,
            icon: (t as any).identifier ?? null,
            entityType: ENTITY_TYPES.WORK_ITEM,
          })
        }
      }
    }

    if (filterType === 'all' || filterType === ENTITY_TYPES.DOCUMENT) {
      for (const p of pages) {
        if (
          p.id !== entityId &&
          !linkedEntityIds.has(p.id) &&
          (!workspaceId || p.envId === workspaceId) &&
          p.title.toLowerCase().includes(query)
        ) {
          results.push({
            id: p.id,
            name: p.title,
            icon: p.icon ?? null,
            entityType: ENTITY_TYPES.DOCUMENT,
          })
        }
      }
    }

    if (filterType === 'all' || filterType === ENTITY_TYPES.PROJECT) {
      for (const pr of projects) {
        if (
          pr.id !== entityId &&
          pr.id !== owningProjectId &&
          !linkedEntityIds.has(pr.id) &&
          (!workspaceId || pr.envId === workspaceId) &&
          pr.name.toLowerCase().includes(query)
        ) {
          results.push({
            id: pr.id,
            name: pr.name,
            icon: pr.icon ?? null,
            entityType: ENTITY_TYPES.PROJECT,
          })
        }
      }
    }

    return results.slice(0, 15)
  }, [debouncedQuery, filterType, tasks, pages, projects, entityId, owningProjectId, linkedEntityIds, workspaceId])

  const handleAddLink = useCallback(
    async (targetId: string, targetType: string, targetName: string, targetIcon: string | null) => {
      if (!workspaceId || !userId) return

      const now = Date.now()
      await createEntityLink({
        id: uuidv7(),
        envId: workspaceId,
        sourceEntityType: entityType,
        sourceEntityId: entityId,
        targetEntityType: targetType,
        targetEntityId: targetId,
        linkType: LINK_TYPES.EXPLICIT,
        isDeleted: false,
        sourceEntityName: entityName,
        sourceEntityIcon: null,
        targetEntityName: targetName,
        targetEntityIcon: targetIcon,
        createdOn: now,
        createdBy: userId,
        updatedOn: now,
        updatedBy: userId,
      })

      setSearchQuery('')
      setIsAdding(false)
    },
    [workspaceId, userId, entityId, entityType, entityName, createEntityLink],
  )

  const handleRemoveLink = useCallback(
    async (linkId: string) => {
      await deleteEntityLink(linkId)
    },
    [deleteEntityLink],
  )

  if (isLoading) return null

  // Group explicit links by target entity type
  const groupedLinks = new Map<string, typeof explicitLinks>()
  for (const link of explicitLinks) {
    const label = getLinkableEntityLabel(link.targetEntityType)
    const group = groupedLinks.get(label)
    if (group) {
      group.push(link)
    } else {
      groupedLinks.set(label, [link])
    }
  }

  return (
    <div>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleSectionHeader
          icon={<Link2 className="size-4" />}
          title="Links"
          count={explicitLinks.length > 0 ? `(${explicitLinks.length})` : undefined}
          isOpen={isOpen}
          rightSlot={
            <Popover open={isAdding} onOpenChange={setIsAdding}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground hover:text-foreground hover:bg-accent"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsAdding(true)
                  }}
                >
                  <Plus className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-3" align="start">
                <div className="space-y-3">
                  <div className="flex gap-1">
                    {['all', ENTITY_TYPES.WORK_ITEM, ENTITY_TYPES.DOCUMENT, ENTITY_TYPES.PROJECT].map(
                      (type) => (
                        <Button
                          key={type}
                          variant={filterType === type ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setFilterType(type)}
                        >
                          {type === 'all' ? 'All' : LINKABLE_ENTITY_LABELS[type as LinkableEntityType]}
                        </Button>
                      ),
                    )}
                  </div>

                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input
                      placeholder="Search entities..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 h-8 text-sm"
                      autoFocus
                    />
                  </div>

                  {searchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-0.5">
                      {searchResults.map((result) => {
                        const Icon = ENTITY_TYPE_ICONS[result.entityType] ?? FileText
                        return (
                          <button
                            key={result.id}
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-accent cursor-pointer truncate flex items-center gap-2"
                            onClick={() =>
                              void handleAddLink(result.id, result.entityType, result.name, result.icon)
                            }
                          >
                            <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                            {result.icon && (
                              <span className="text-muted-foreground text-xs">{result.icon}</span>
                            )}
                            <span className="truncate">{result.name || 'Untitled'}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Type at least 3 characters to search
                    </p>
                  )}

                  {debouncedQuery.trim().length >= 3 && searchResults.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No results found
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          }
        />
        <CollapsibleContent className="pt-2">
          {explicitLinks.length === 0 ? (
            <p className="text-sm text-muted-foreground px-2 py-1">
              No links yet
            </p>
          ) : (
            <div className="space-y-2">
              {Array.from(groupedLinks.entries()).map(([label, groupLinks]) => (
                <div key={label} className="space-y-0.5">
                  <p className="text-xs font-medium text-muted-foreground px-2 uppercase tracking-wide">
                    {label}
                  </p>
                  {groupLinks.map((link) => {
                    const Icon = ENTITY_TYPE_ICONS[link.targetEntityType] ?? FileText
                    return (
                      <div
                        key={link.id}
                        className="group flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent"
                      >
                        <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                        <button
                          type="button"
                          className={
                            'flex-1 text-left text-sm truncate cursor-pointer ' +
                            (link.resolvedTargetIsArchived
                              ? 'text-muted-foreground line-through'
                              : '')
                          }
                          onClick={() => onLinkClick?.(link.targetEntityId, link.targetEntityType)}
                        >
                          <LinkEntityIcon link={link} />
                          {link.resolvedTargetName || 'Untitled'}
                        </button>
                        {link.resolvedTargetIsArchived && (
                          <span
                            className="inline-flex items-center gap-1 shrink-0 rounded-full bg-secondary text-secondary-foreground px-2 py-0.5 text-[10px] font-medium"
                            title="Archived"
                          >
                            <Archive className="size-3" />
                            Archived
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => void handleRemoveLink(link.id)}
                        >
                          <X className="size-3" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
